import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import type { SearchMatch } from '@festina-life/ui/search'
import { useCallback, useMemo } from 'react'

import type { IndexedParameterDefinitionCreateOperations } from '../../uiModels/parameterOperations/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { configurationItemsMapToList } from '../errorsPanel/helpers'
import {
  expressionMatchesForQueryInParameterDefinitionOperation,
  groupSearchResults,
  valueOnlyParameterSearchItems,
} from './helpers'
import {
  type ConfigurationItemSearchFunctions,
  type ConfigurationItemSearchMatch,
  type ConfigurationItemSearchResult,
  SearchCategories,
  type SearchCategoryKey,
} from './types'

const toConfigurationItemSearchResult =
  (matches?: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]) =>
  (
    operationAtDate: IndexedParameterDefinitionCreateOperations['operations'][number],
  ): ConfigurationItemSearchResult => {
    assertNotEmpty(operationAtDate?.internalOperation.entity?.name)

    const parameterId = operationAtDate.internalOperation.entity?.name

    return {
      value: {
        id: parameterId,
        operationType: 'parameterDefinition',
        type: operationAtDate.internalOperation.entity.parameterType,
        isDraft: operationAtDate.isDraft,
        packageName: operationAtDate.internalOperation.entity.packageName ?? '',
        layer: operationAtDate.layer,
        block: operationAtDate.block,
      },
      matches,
    }
  }

export const useParameterSearch = (): ConfigurationItemSearchFunctions => {
  const {
    parameterDefinitionCreateOperations,
    parameterValueEntityOperations,
  } = useOperationsContext()
  const translate = useTranslate()
  const nonAnnuledParameterDefinitions = useMemo(
    () =>
      configurationItemsMapToList(parameterDefinitionCreateOperations)
        .flatMap(({ operations }) => operations)
        .filter(({ annulOperations }) => !annulOperations.length),
    [parameterDefinitionCreateOperations],
  )

  const valueOnlyParameters = useMemo(
    () =>
      valueOnlyParameterSearchItems({
        definitionOperations: nonAnnuledParameterDefinitions,
        parameterValueEntityOperations,
        fallbackPackageName: translate(
          'parametersTree.fallbackPackage.parameters',
        ),
      }),
    [nonAnnuledParameterDefinitions, parameterValueEntityOperations, translate],
  )

  const searchByParameterName = useCallback(
    (query: string) => {
      const needle = query.toLocaleLowerCase().trim()

      return Promise.resolve([
        ...nonAnnuledParameterDefinitions
          .filter(
            (operationAtDate) =>
              !!operationAtDate?.internalOperation?.entity?.name
                ?.toLocaleLowerCase()
                .includes(needle),
          )
          .map(toConfigurationItemSearchResult()),
        ...valueOnlyParameters
          .filter(({ id }) => id.toLocaleLowerCase().includes(needle))
          .map((value) => ({ value })),
      ])
    },
    [nonAnnuledParameterDefinitions, valueOnlyParameters],
  )

  const searchByProperties = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        nonAnnuledParameterDefinitions
          .map((operationAtDate) => {
            const matches: SearchMatch<
              ConfigurationItemSearchMatch,
              SearchCategoryKey
            >[] = []
            const enumName = operationAtDate?.internalOperation.entity?.name

            assertNotEmpty(enumName)

            operationAtDate?.internalOperation.entity?.dimensionDefinitions?.forEach(
              (dimensionDefinition) => {
                if (
                  dimensionDefinition?.name &&
                  dimensionDefinition.name.toLocaleLowerCase().includes(needle)
                ) {
                  const path = `${enumName}.${dimensionDefinition.name}`
                  matches.push({
                    categories: [SearchCategories.properties],
                    path,
                    value: {
                      propertyPath: path,
                      isExpression: false,
                      match: dimensionDefinition.name,
                    },
                  })
                }
              },
            )

            if (
              operationAtDate?.internalOperation.entity?.parameterType
                ?.toLocaleLowerCase()
                .includes(needle)
            ) {
              matches.push({
                categories: [SearchCategories.properties],
                path: `${enumName}.${translate('search.enum.parameterType')}`,
                value: {
                  isExpression: false,
                  match:
                    operationAtDate?.internalOperation.entity?.parameterType,
                },
              })
            }

            if (
              operationAtDate?.internalOperation.entity?.environmentType
                ?.toLocaleLowerCase()
                .includes(needle)
            ) {
              matches.push({
                categories: [SearchCategories.properties],
                path: `${enumName}.${translate('search.enum.environmentType')}`,
                value: {
                  isExpression: false,
                  match:
                    operationAtDate?.internalOperation.entity?.environmentType,
                },
              })
            }

            return [operationAtDate, matches] as const
          })
          .filter(([, matches]) => !!matches.length)
          .map(([parameterOperation, matches]) =>
            toConfigurationItemSearchResult(matches)(parameterOperation),
          ),
      )
    },
    [nonAnnuledParameterDefinitions, translate],
  )

  const searchByFPlus = useCallback(
    (query: string) => {
      const needle = query.toLocaleLowerCase().trim()

      return Promise.resolve(
        nonAnnuledParameterDefinitions
          .filter(notEmpty)
          .map((operationAtDate) =>
            expressionMatchesForQueryInParameterDefinitionOperation(
              operationAtDate,
              needle,
            ),
          )
          .filter(([, matches]) => matches.length > 0)
          .map(([operation, matches]) =>
            toConfigurationItemSearchResult(matches)(operation),
          ),
      )
    },
    [nonAnnuledParameterDefinitions],
  )

  const searchByClass = useCallback(() => {
    throw new Error('Searching parameters by class is unavailable')
  }, [])

  const searchAll = useCallback(
    async (query: string) => {
      const allByName = await searchByParameterName(query)
      const allByProperties = await searchByProperties(query)
      const allByFPlus = await searchByFPlus(query)

      return groupSearchResults(...allByName, ...allByProperties, ...allByFPlus)
    },
    [searchByFPlus, searchByParameterName, searchByProperties],
  )

  return {
    name: searchByParameterName,
    className: searchByClass,
    fplus: searchByFPlus,
    properties: searchByProperties,
    all: searchAll,
  }
}
